import { extractApiError } from "@/lib/api";

/**
 * Map a CATALOGUE-write refusal to a sentence the practitioner can read, in their own
 * language — the admin-plane mirror of lib/checkoutErrors.js.
 *
 * The backend answers 422 `{"detail": {"code", "message"}}` when a catalogue row (product,
 * programme, course, invoice, promo code, gift card, booking service) is refused by
 * `platform_helpers.resolved_currency`. The CODE is the contract; the `message` is an
 * English developer diagnostic ("this platform settles in USD, so a price cannot be
 * denominated in CAD") and must never reach a French or Spanish practitioner — which is
 * exactly what happened when every editor rendered `extractApiError`'s `detail.message`.
 *
 * Why not inside extractApiError: lib/apiErrors.js keeps codes that a caller maps locally
 * OUT of its own table, so the wording of one code cannot drift between two screens.
 * This module is that local mapper for the seven catalogue editors.
 *
 * Pure, and takes `tc` (a translator bound to the "common" namespace) as an argument:
 * `errors.*` live in the common dictionary, and the i18n lookup never falls through to
 * another namespace — passing the admin `t` would render the English fallback.
 *
 * @param {object} e         axios error
 * @param {function} tc      translator bound to the "common" namespace
 * @param {string} fallback  screen-level generic message
 * @returns {string}
 */
export function catalogueErrorMessage(e, tc, fallback = "") {
  const detail = e?.response?.data?.detail;
  // A request-model rule (a pydantic validator) cannot answer `{code}`: it answers FastAPI's
  // 422 ARRAY, and carries its code as the item's `type` (PydanticCustomError). The first
  // item whose type this table knows is the refusal to name.
  const code = Array.isArray(detail)
    ? detail.map((it) => it?.type).find((type) => CATALOGUE_ERROR_KEYS[type])
    : detail && typeof detail === "object" ? detail.code : detail;
  const key = CATALOGUE_ERROR_KEYS[code];
  if (key) return tc(key, CATALOGUE_ERROR_VARS[code]?.(detail));
  return extractApiError(e, fallback, tc) || fallback;
}

/** `{pathways: "A, B"}` from a refusal naming the pathways that block it. */
const pathwayTitles = (detail) => ({
  pathways: (Array.isArray(detail?.pathways) ? detail.pathways : [])
    .map((p) => (p && typeof p.title === "string" ? p.title : ""))
    .filter(Boolean)
    .join(", "),
});

// The interpolation variables a code's sentence takes, read from the refusal's own detail.
const CATALOGUE_ERROR_VARS = {
  COURSE_IN_PATHWAY: pathwayTitles,
};

const CATALOGUE_ERROR_KEYS = {
  // `resolved_currency` refused a NEW denomination outside the platform's settlement
  // currency. The editors state that currency and offer no other, so this is reachable
  // only from a stale form or a hand-built request — but when it is, the practitioner
  // reads a sentence, not an env-var name.
  CURRENCY_NOT_SETTLEABLE: "errors.currencyNotSettleable",
  // SHEET-225 — `Lesson._quiz_requires_title` (learn_telehealth_routes.py) refused a quiz
  // with a blank title. The course editor checks this before sending; this is what a
  // publish toggle on a course stored before the rule existed reads.
  QUIZ_TITLE_REQUIRED: "errors.quizTitleRequired",
  // A course delete, or an edit that would unpublish it, refused while pathways need it
  // (learn_telehealth_routes: delete_course / update_course). The detail names those
  // pathways, and the sentence names them in turn.
  COURSE_IN_PATHWAY: "errors.courseInPathway",
  // A save that would leave a PUBLISHED course with no lessons (publishing an empty
  // course, or removing the last lesson of a published one), refused by
  // `modules/course_rules`. The editor checks this first; this is the stale-form and
  // hand-built-request path.
  COURSE_HAS_NO_LESSONS: "errors.courseHasNoLessons",
};
